import { Fragment, useCallback, useEffect, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  alpha,
} from '@mui/material';
import { MoreVertical, Sparkles } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type { Cma_Report_Source_Enum_Enum } from '@graphql/__generated__/graphql';
import AlertPropertyAppraisable from '@modules/_shared/components/AlertPropertyAppraisable';
import { AppraisalHistoryDialog } from '@modules/_shared/components/appraisal/AppraisalHistoryDialog';
import { GET_APPRAISAL_HISTORY } from '@modules/_shared/components/appraisal/appraisalQueries';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { TemplateViewerDialog } from '@modules/_shared/components/TemplateViewerDialog';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { usePropertyAppraisal } from '@modules/_shared/hooks/useAppraiseProperty';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  formatAppraisalPrice,
  formatCmaReportPrice,
  hasValidAppraisalData,
} from '@modules/_shared/utils/formatting';
import { userHasClaimedLeadAssignment } from '@modules/_shared/utils/listingPermissions';
import { useGetDefaultApolloTemplate } from '@modules/_shared/utils/template';
import { GenerateCMAPreviewButton } from '@modules/lead-details/components/LeadDetailsCMAPreview';
import { useLeadPermissions } from '@modules/lead-details/hooks/useLeadPermissions';
import type { LeadDetailsDrawer } from '@modules/lead-details/type';
import { AppraisalGraph } from '@modules/leads/AppraisalGraph';
import { useAnalytics } from '@shared/analytics';
import { ANALYTICS_EVENTS } from '@shared/analytics/constants';
import { getBaseCmaEventProperties } from '@shared/analytics/posthog-helpers';
import { useLocale } from '@shared/hooks/locale';
import { getCurrencyByCountryCode } from '@shared/LocaleContext';
import { toGlobalId } from '@shared/utils/global-id';

const GET_VALUATION_DATA = gql(/* GraphQL */ `
  query GetValuationDataForLeadDetails($propertyId: uuid!) {
    property_appraisable(property_id: $propertyId) {
      appraisable {
        result
        errors {
          path
        }
      }
    }
    doc_templates(
      where: { type: { _eq: appraisal_report }, active: { _eq: true } }
    ) {
      id
      name
      active
      ...DocTemplateDefaultLinks
    }
  }
`);

type LeadDetailsValuationCardProps = {
  lead: LeadDetailsDrawer;
  refetch: () => Promise<unknown>;
  onCmaPreviewGenerated: (cmaReportId: string) => void;
  isPreviewOpen?: boolean;
  onClosePreview?: () => void;
};

export const LeadDetailsValuationCard = ({
  lead,
  refetch,
  onCmaPreviewGenerated,
  isPreviewOpen = false,
  onClosePreview,
}: LeadDetailsValuationCardProps) => {
  const { t, locale } = useLocale();
  const { me } = useAppData();
  const { track } = useAnalytics();

  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);

  const [valuationMenuAnchor, setValuationMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [showGraph, setShowGraph] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  const property = lead.property;
  const appraisal = property?.appraisals?.[0] ?? null;
  const propertyId = property?.id ?? null;
  const currency = getCurrencyByCountryCode(property?.country_code ?? 'CH');

  // Permission checks
  const { canManageLead, isAdmin, restrictedAgentView } = useLeadPermissions({
    lead,
  });
  const canManageValuation = canManageLead;

  const allCmaReports = lead.cma_reports ?? [];

  const hasOnlineValuation = hasValidAppraisalData(appraisal);

  const isLeadAgentOrTeamLeader = userHasClaimedLeadAssignment(
    me,
    lead.lead_agents ?? [],
  );

  const { data: valuationData, loading: propertyAppraisableLoading } = useQuery(
    GET_VALUATION_DATA,
    {
      variables: {
        propertyId: propertyId ?? '',
      },
      skip: propertyId == null,
    },
  );

  const {
    data: appraisalHistoryData,
    loading: appraisalHistoryLoading,
    refetch: refetchAppraisalHistory,
  } = useQuery(GET_APPRAISAL_HISTORY, {
    variables: { propertyId: propertyId ?? '' },
    skip: !propertyId || !historyDialogOpen,
  });

  const { getDefaultTemplate } = useGetDefaultApolloTemplate();
  const appraisalTemplates = valuationData?.doc_templates ?? [];
  const defaultTemplate = getDefaultTemplate(appraisalTemplates);
  const templateId = defaultTemplate?.id ?? null;

  const propertyAppraisable = valuationData?.property_appraisable?.appraisable;
  const isPropertyAppraisable = propertyAppraisable?.result ?? false;
  const propertyAppraisableErrors = propertyAppraisable?.errors ?? [];

  const {
    appraiseProperty,
    mutationLoading: valuationLoading,
    queryError,
    mutationError,
  } = usePropertyAppraisal(appraisal?.id ?? undefined);

  useEffect(() => {
    setErrorLike(queryError ?? mutationError ?? null);
  }, [queryError, mutationError]);

  // Validation state
  const showMissingFields =
    !hasOnlineValuation &&
    !propertyAppraisableLoading &&
    propertyAppraisableErrors.length > 0;

  const canTriggerValuation =
    Boolean(propertyId) &&
    isPropertyAppraisable &&
    canManageValuation &&
    !restrictedAgentView;

  const handleOpenCmaReport = useCallback(
    (
      cmaReportId: string,
      reportSource: Cma_Report_Source_Enum_Enum | undefined | null,
    ) => {
      if (restrictedAgentView) {
        return;
      }

      // Track preview opened
      const baseProperties = getBaseCmaEventProperties(me, cmaReportId);
      if (baseProperties) {
        track(ANALYTICS_EVENTS.CMA_PREVIEW_OPENED, {
          ...baseProperties,
          report_source: reportSource ?? 'unknown',
          lead_id: lead.id,
        });
      }

      // Open using the same callback as when generating a new CMA
      onCmaPreviewGenerated(cmaReportId);
    },
    [restrictedAgentView, me, track, lead.id, onCmaPreviewGenerated],
  );

  const handleTriggerValuation = useCallback(async () => {
    if (!propertyId || !canTriggerValuation || restrictedAgentView) {
      return;
    }

    await appraiseProperty(propertyId);
    await refetch();
    await refetchAppraisalHistory();
  }, [
    restrictedAgentView,
    appraiseProperty,
    canTriggerValuation,
    propertyId,
    refetch,
    refetchAppraisalHistory,
  ]);

  return (
    <>
      <ListingAccordion
        title={t('Property valuation')}
        defaultExpanded
        disableCollapse
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Show CMA reports if available */}
          {allCmaReports.map((cmaReport, index) => (
            <Fragment key={cmaReport.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {formatCmaReportPrice(cmaReport, currency, locale)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    {t('CMA reports')}
                    {cmaReport.created_at && (
                      <>
                        {' • '}
                        <TimeAgo addSuffix strict={false}>
                          {cmaReport.created_at}
                        </TimeAgo>
                      </>
                    )}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() =>
                    handleOpenCmaReport(cmaReport.id, cmaReport.report_source)
                  }
                  disabled={restrictedAgentView}
                  sx={theme => ({
                    px: 2,
                    py: 0.5,
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                    color: (theme.vars ?? theme).palette.primary.main,
                    '&:hover': {
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                    },
                  })}
                >
                  {t('Open')}
                </Button>
              </Box>

              {index < allCmaReports.length - 1 && <Divider />}
            </Fragment>
          ))}

          {allCmaReports.length > 0 && hasOnlineValuation ? <Divider /> : null}

          {hasOnlineValuation ? (
            <>
              {/* Show online valuation */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {formatAppraisalPrice(appraisal, currency, locale)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    {t('Online valuation')}
                    {appraisal?.updated_at ? (
                      <>
                        {' • '}
                        <TimeAgo addSuffix strict={false}>
                          {appraisal.updated_at}
                        </TimeAgo>
                      </>
                    ) : null}
                  </Typography>
                </Box>
                {valuationLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <IconButton
                    onClick={event =>
                      setValuationMenuAnchor(event.currentTarget)
                    }
                    disabled={restrictedAgentView}
                    size="small"
                  >
                    <MoreVertical size={18} />
                  </IconButton>
                )}
              </Box>

              {showGraph && appraisal?.id && propertyId && (
                <AppraisalGraph
                  latestAppraisalId={appraisal.id}
                  propertyId={propertyId}
                  canAppraise={false}
                />
              )}
            </>
          ) : (
            // No valuation exists - show appraise button or validation errors
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {propertyAppraisableLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : null}
              {showMissingFields ? (
                <AlertPropertyAppraisable
                  errors={propertyAppraisableErrors}
                  isPropertyTypeMissing={property?.property_type == null}
                />
              ) : null}
              {propertyId && canManageValuation ? (
                <Button
                  variant="contained"
                  loading={valuationLoading}
                  onClick={handleTriggerValuation}
                  disabled={!canTriggerValuation}
                  loadingPosition="start"
                  startIcon={<Sparkles />}
                  fullWidth
                >
                  {t('Appraise')}
                </Button>
              ) : null}
            </Box>
          )}

          {hasOnlineValuation && allCmaReports.length === 0 ? (
            <GenerateCMAPreviewButton
              leadId={lead.id}
              disabled={restrictedAgentView}
              isPreviewOpen={isPreviewOpen}
              onSuccess={onCmaPreviewGenerated}
              onClose={onClosePreview}
              onError={setErrorLike}
              refetch={refetch}
            />
          ) : null}
        </Box>
      </ListingAccordion>
      <Menu
        anchorEl={valuationMenuAnchor}
        open={Boolean(valuationMenuAnchor)}
        onClose={() => setValuationMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            setValuationMenuAnchor(null);
            setReportDialogOpen(true);
          }}
          disabled={templateId == null}
        >
          {t('Open report')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setValuationMenuAnchor(null);
            setShowGraph(prev => !prev);
          }}
        >
          {showGraph ? t('Hide graph') : t('Show graph')}
        </MenuItem>
        {(isAdmin || isLeadAgentOrTeamLeader) && (
          <MenuItem
            onClick={() => {
              setValuationMenuAnchor(null);
              setHistoryDialogOpen(true);
            }}
          >
            {t('View history')}
          </MenuItem>
        )}
        {canTriggerValuation && (
          <MenuItem
            onClick={() => {
              setValuationMenuAnchor(null);
              handleTriggerValuation();
            }}
          >
            {t('Reappraise')}
          </MenuItem>
        )}
      </Menu>
      {templateId && (
        <TemplateViewerDialog
          templateId={toGlobalId('DocTemplate', templateId)}
          documentId={toGlobalId('Lead', lead.id)}
          open={reportDialogOpen}
          onClose={() => setReportDialogOpen(false)}
        />
      )}
      <AppraisalHistoryDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        loading={appraisalHistoryLoading}
        appraisals={appraisalHistoryData?.appraisals ?? []}
        currency={currency}
        locale={locale}
      />
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </>
  );
};
